import {useCallback, useState} from 'react'
import {View} from 'react-native'
import {AtUri} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {Trans, useLingui} from '@lingui/react/macro'
import {useQueryClient} from '@tanstack/react-query'

import {
  CAPSULE_ALL_SUBJECTS,
  CAPSULE_CATEGORIES,
  CAPSULE_GRADES,
  categoryOfLevel,
  getAvailableSubjects,
} from '#/lib/curriculum'
import {
  type FlashBookAudience,
  flashbookAudience,
  flashbookPostFacts,
  flashbookPostRecord,
  flashbookPublishTarget,
} from '#/lib/flashbookPublish'
import {createActorFlashbooksQueryKey} from '#/state/queries/flashbook'
import {useAgent} from '#/state/session'
import * as Toast from '#/view/com/util/Toast'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {ClassroomAudienceStep} from '#/components/classrooms/AudienceStep'
import * as Dialog from '#/components/Dialog'
import {
  countExercises,
  uploadFlashBookMedia,
} from '#/components/flashbooks/flashbookSave'
import {Check_Stroke2_Corner0_Rounded as CheckThin} from '#/components/icons/Check'
import {PaperPlane_Stroke2_Corner0_Rounded as PaperPlane} from '#/components/icons/PaperPlane'
import {TimesLarge_Stroke2_Corner0_Rounded as X} from '#/components/icons/Times'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'
import {type FlashBookCover, type FlashBookPage} from '#/types/flashbook'

/**
 * Publishes a FlashBook: uploads cover media + reading-audio blobs, creates
 * the base post (type: 'flashbook'), writes the app.bsky.feed.flashbook
 * record referencing the post, and patches the post with the back-ref.
 *
 * When the book came from a saved draft (`draftUri`), the existing record is
 * updated in place — same rkey, `status: 'draft'` → `'published'` — instead of
 * a second record being created, so publishing converts the draft rather than
 * leaving an orphan behind. Nothing else has to change: the AppView's indexer
 * treats an update of a not-yet-indexed record as a plain insert (drafts carry
 * no `post`, so they are never indexed), so the converted record lands in the
 * feed by the same path a fresh publish does.
 *
 * Opened from a classroom (`classroomName`), the last step asks *who the book is
 * for* and can publish it to that room alone. The two answers write the same two
 * records and differ only in what those records *say* (see `flashbookAudience`),
 * which is the same arrangement a capsule has — one kind of work, one way of
 * making it, and an audience that decides where it may be shown.
 */
export interface PublishedFlashBook {
  /** The `app.bsky.feed.flashbook` record. */
  flashbookUri: string
  flashbookCid: string
  /** The post it was published as. A book is always both. */
  postUri: string
  postCid: string
  /**
   * Whether this was published for one classroom.
   *
   * The caller cannot tell from the addresses — both audiences write the same two
   * records — so the audience the creator chose travels back with them, and the
   * room records the work as its own from this answer rather than from a guess.
   */
  forClassroom?: boolean
}
export function PublishFlashBookDialog({
  control,
  title,
  description,
  cover,
  pages,
  themeId,
  backgroundMusicId,
  htmlThemeUrl,
  draftUri,
  draftCreatedAt,
  classroomName,
  lockedLevel,
  onSuccess,
}: {
  control: Dialog.DialogControlProps
  title: string
  description: string
  cover: FlashBookCover
  pages: FlashBookPage[]
  themeId?: string
  /** The bed, stored apart from the theme; `'none'` is deliberate silence. */
  backgroundMusicId?: string
  htmlThemeUrl?: string
  /** AT-URI of the draft this book was loaded from, if any. */
  draftUri?: string | null
  /** That draft's `createdAt`, preserved so the book keeps its original date. */
  draftCreatedAt?: string | null
  /**
   * The classroom this studio was opened from, when it was opened from one.
   *
   * It is what puts the choice on the last step: a creator writing a book inside
   * a room is the only one with a classroom to publish *to*. Every other way into
   * the studio leaves it undefined and publishes as the studio always has.
   */
  classroomName?: string
  /**
   * The level this publish is fixed at, when the book is being made for a room
   * that has one.
   *
   * A book written inside a classroom is filed by that classroom, exactly as a
   * capsule is (see `PublishCapsuleDialog`): the category and the year are shown
   * as the room's answers with no way to clear them, because a book at another
   * level is one the room does not hold.
   */
  lockedLevel?: string
  /**
   * What the publish wrote.
   *
   * Something outside the studio has a use for the addresses: a classroom the
   * book was made in puts it in the room (see `#/screens/FlashBookStudio`), and
   * it cannot guess either one.
   */
  onSuccess?: (published: PublishedFlashBook) => void
}) {
  return (
    <Dialog.Outer control={control}>
      <DialogInner
        control={control}
        title={title}
        description={description}
        cover={cover}
        pages={pages}
        themeId={themeId}
        backgroundMusicId={backgroundMusicId}
        htmlThemeUrl={htmlThemeUrl}
        draftUri={draftUri}
        draftCreatedAt={draftCreatedAt}
        classroomName={classroomName}
        lockedLevel={lockedLevel}
        onSuccess={onSuccess}
      />
    </Dialog.Outer>
  )
}

function DialogInner({
  control,
  title,
  description,
  cover,
  pages,
  themeId,
  backgroundMusicId,
  htmlThemeUrl,
  draftUri,
  draftCreatedAt,
  classroomName,
  lockedLevel,
  onSuccess,
}: {
  control: Dialog.DialogControlProps
  title: string
  description: string
  cover: FlashBookCover
  pages: FlashBookPage[]
  themeId?: string
  backgroundMusicId?: string
  htmlThemeUrl?: string
  draftUri?: string | null
  draftCreatedAt?: string | null
  classroomName?: string
  lockedLevel?: string
  onSuccess?: (published: PublishedFlashBook) => void
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  /*
   * The room's lock, when there is one — the same rule the capsule publisher
   * keeps: the work is the room's, so its level is the room's.
   */
  const lock =
    lockedLevel && categoryOfLevel(lockedLevel) ? lockedLevel : undefined
  const [selectedCategory, setSelectedCategory] = useState<string | null>(() =>
    categoryOfLevel(lock),
  )
  const [selectedGrade, setSelectedGrade] = useState<string | null>(
    lock ?? null,
  )
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null)
  /** Public unless the creator says otherwise: it is what a book was. */
  const [audience, setAudience] = useState<FlashBookAudience>('public')
  const forClassroom = !!classroomName && audience === 'classroom'

  const agent = useAgent()
  const queryClient = useQueryClient()
  const [isPending, setPending] = useState(false)
  const [isSuccess, setSuccess] = useState(false)

  const onPublish = useCallback(async () => {
    setPending(true)
    try {
      const did = agent.session!.did
      const now = new Date().toISOString()

      // 0. Upload cover media + reading audio (shared with draft saving).
      const {
        cover: coverOut,
        pages: pagesOut,
        videoEmbed,
      } = await uploadFlashBookMedia({agent, i18n, did, cover, pages})

      /*
        What the two records say about who the book is for.

        Read once, here, and spread onto the record below so the *same* post
        record goes both to the create and to the patch — a mark added to the
        patch alone would be missing from a book whose second write failed, and
        that is the one field whose absence leaks a room's own book into the
        public feed (see `flashbookAudience`).
      */
      const marks = flashbookAudience(forClassroom)

      const postText = title + (description ? '\n\n' + description : '')
      const postRecord = flashbookPostRecord({
        text: postText,
        createdAt: now,
        embed: videoEmbed,
        visibility: marks.postVisibility,
      })

      // 1. Create the base post.
      const postRes = await agent.com.atproto.repo.createRecord({
        repo: did,
        collection: 'app.bsky.feed.post',
        record: postRecord,
      })

      // 2. Write the flashbook record referencing the post. A book loaded from
      // a draft updates that draft's own record (same rkey) so it is converted
      // in place; anything else creates a fresh record.
      const exerciseCount = countExercises(pagesOut)
      const bookThemeId = themeId ?? 'storybook'
      const flashbookRecord = {
        $type: 'app.bsky.feed.flashbook',
        title,
        description,
        status: 'published',
        /**
         * Who the book was written for, said on the record.
         *
         * It is the field the app reads to keep a room's own book out of its own
         * feeds — the same field and the same value a capsule carries — and the
         * one a backend would enforce the rule with once classrooms live on a
         * server rather than on one device.
         */
        ...(marks.recordVisibility
          ? {visibility: marks.recordVisibility}
          : null),
        themeId: bookThemeId,
        // Its own field, never folded into the theme: they are two settings
        // that are changed independently, and one must not imply the other.
        backgroundMusicId,
        behaviorSettings: htmlThemeUrl ? {htmlThemeUrl} : undefined,
        cover: coverOut,
        pages: pagesOut,
        pageCount: pagesOut.length,
        exerciseCount,
        level: selectedGrade ?? undefined,
        subject: selectedSubject ?? undefined,
        post: {uri: postRes.data.uri, cid: postRes.data.cid},
        // The record is replaced wholesale, so the book's original creation
        // date has to be carried over by hand.
        createdAt: draftCreatedAt ?? now,
      }
      const target = flashbookPublishTarget(draftUri)
      const flashbookRes =
        target.mode === 'update'
          ? await agent.com.atproto.repo.putRecord({
              repo: did,
              collection: 'app.bsky.feed.flashbook',
              rkey: target.rkey,
              record: flashbookRecord,
            })
          : await agent.com.atproto.repo.createRecord({
              repo: did,
              collection: 'app.bsky.feed.flashbook',
              record: flashbookRecord,
            })

      /*
       * 3. Patch the post with the flashbook back-ref, and with the three facts
       * the feed card can state before the book itself has arrived.
       *
       * The card is drawn in a scrolling feed, where a row that is blank until a
       * second request lands reads as a broken post. These are the numbers a
       * reader wants anyway — how long the book is, how much of it is work, and
       * which theme it was written in — so they ride on the post that names the
       * book, exactly as a capsule post carries its exercise count. The card is
       * complete on the first paint and gains the cover when the record loads.
       *
       * `flashbookPostFacts` writes them, because the fixture that stands in for a
       * published post in the browser suite reads them the same way: one definition
       * of what a post says about its book, rather than two that happen to agree
       * today.
       */
      const postRkey = new AtUri(postRes.data.uri).rkey
      const patched = await agent.com.atproto.repo.putRecord({
        repo: did,
        collection: 'app.bsky.feed.post',
        rkey: postRkey,
        record: {
          ...postRecord,
          ...flashbookPostFacts({
            bookUri: flashbookRes.data.uri,
            bookCid: flashbookRes.data.cid,
            pageCount: pagesOut.length,
            exerciseCount,
            themeId: bookThemeId,
            cover: coverOut,
          }),
        },
      })

      /*
       * The author's own list of books is cached, and this just changed it.
       *
       * A profile's Flashbooks tab is built from those records (see
       * `#/lib/profileWork`), and the studio is often opened *from* such a page —
       * so without this the creator comes back to a grid that does not contain the
       * book they just published until the cache goes stale. Their own list only:
       * nobody else's changed.
       */
      void queryClient.invalidateQueries({
        queryKey: createActorFlashbooksQueryKey(did),
      })

      setSuccess(true)
      Toast.show(
        forClassroom
          ? l`FlashBook published into the classroom!`
          : l`FlashBook published!`,
      )
      setTimeout(() => {
        control.close()
        onSuccess?.({
          flashbookUri: flashbookRes.data.uri,
          flashbookCid: flashbookRes.data.cid,
          postUri: patched.data.uri,
          postCid: patched.data.cid,
          ...(forClassroom ? {forClassroom: true} : null),
        })
      }, 1500)
    } catch (e: unknown) {
      console.error('Failed to publish flashbook', e)
      const message = e instanceof Error ? e.message : undefined
      Toast.show(message || l`Failed to publish flashbook`)
      setPending(false)
    }
  }, [
    agent,
    control,
    cover,
    description,
    draftCreatedAt,
    draftUri,
    forClassroom,
    i18n,
    l,
    onSuccess,
    pages,
    selectedGrade,
    selectedSubject,
    title,
    themeId,
    backgroundMusicId,
    htmlThemeUrl,
  ])

  const categoryLabel = CAPSULE_CATEGORIES.find(
    c => c.value === selectedCategory,
  )?.label
  const gradeLabel = (
    selectedCategory ? CAPSULE_GRADES[selectedCategory] : []
  ).find(g => g.value === selectedGrade)?.label
  const subjectLabel = CAPSULE_ALL_SUBJECTS.find(
    s => s.value === selectedSubject,
  )?.label

  return (
    <Dialog.ScrollableInner
      label={l`Publish FlashBook`}
      style={[a.w_full, {maxWidth: 500}]}>
      {/*
        The sheet's own hook, on an element that actually reaches the DOM: a
        `testID` on `ScrollableInner` itself is dropped by the dialog chrome, so
        a browser had nothing to find here even while the sheet was open.
      */}
      <View testID="publishFlashBookDialogInner" style={[a.gap_2xl, a.pt_md]}>
        <View style={[a.gap_sm, a.align_center]}>
          <View style={[a.flex_row, a.align_center, a.gap_sm]}>
            <Text style={[a.text_2xl]}>📖</Text>
            <Text style={[a.text_lg, a.font_bold]}>
              <Trans>Publish your FlashBook</Trans>
            </Text>
          </View>
          <Text
            style={[a.text_sm, a.text_center, t.atoms.text_contrast_medium]}>
            <Trans>
              {pages.length} pages · {title}
            </Trans>
          </Text>
        </View>

        {/* The room's level, said where it cannot be missed (see the capsule publisher). */}
        {lock && classroomName ? (
          <View
            testID="flashbookPublishLevelLock"
            style={[a.p_md, a.rounded_md, t.atoms.bg_contrast_25]}>
            <Text style={[a.text_sm, t.atoms.text]}>
              {l`This book is published at ${l(gradeLabel ?? msg`Unknown`)} — the level of ${classroomName}.`}
            </Text>
          </View>
        ) : null}

        {/* Step 1 – Who Are You Targeting? */}
        <View style={[a.gap_md]}>
          <Text style={[a.text_md, a.font_bold]}>
            <Trans>Who are you targeting?</Trans>
          </Text>
          {selectedCategory ? (
            <View style={[a.flex_row, a.align_center, a.justify_between]}>
              <Text style={[a.flex_1, a.text_sm]}>
                {l(categoryLabel ?? msg`Unknown`)}
                {selectedGrade ? ` · ${l(gradeLabel ?? msg`Unknown`)}` : ''}
                {selectedSubject ? ` · ${l(subjectLabel ?? msg`Unknown`)}` : ''}
              </Text>
              {lock ? null : (
                <Button
                  label={l(msg`Clear selection`)}
                  size="tiny"
                  variant="solid"
                  color="secondary"
                  shape="round"
                  onPress={() => {
                    setSelectedCategory(null)
                    setSelectedGrade(null)
                    setSelectedSubject(null)
                  }}>
                  <ButtonIcon icon={X} />
                </Button>
              )}
            </View>
          ) : (
            <View style={[a.gap_sm]}>
              {CAPSULE_CATEGORIES.map(c => (
                <OptionRow
                  key={c.value}
                  label={l(c.label)}
                  onSelect={() => setSelectedCategory(c.value)}
                />
              ))}
            </View>
          )}
          {selectedCategory && !selectedGrade && (
            <View style={[a.gap_sm]}>
              {(CAPSULE_GRADES[selectedCategory] ?? []).map(g => (
                <OptionRow
                  key={g.value}
                  label={l(g.label)}
                  onSelect={() => setSelectedGrade(g.value)}
                />
              ))}
            </View>
          )}
          {selectedCategory && selectedGrade && !selectedSubject && (
            <View style={[a.gap_sm]}>
              {getAvailableSubjects(selectedGrade).map(s => (
                <OptionRow
                  key={s.value}
                  label={l(s.label)}
                  onSelect={() => setSelectedSubject(s.value)}
                />
              ))}
            </View>
          )}
        </View>

        {/* Where the book goes, asked where it can still be answered. */}
        {classroomName ? (
          <ClassroomAudienceStep
            testIDPrefix="flashbookAudience"
            question={l`Who can read it?`}
            publicTitle={l`Anyone — publish it to your profile`}
            publicDescription={l`It goes on your profile and into the feed, where anyone can find it, read it and share it into their own classrooms.`}
            classroomTitle={l`Only ${classroomName}`}
            classroomDescription={l`It belongs to this classroom. It stays out of your profile, out of every feed and out of the shelf — its members read it, and it is marked as the room's own wherever it goes.`}
            note={l`Its cover film comes with it, and it is a post like any other inside the classroom: its members can read it, comment on it, react to it and share it. Outside the classroom the app keeps it out of sight.`}
            forClassroom={forClassroom}
            onChange={wantsClassroom =>
              setAudience(wantsClassroom ? 'classroom' : 'public')
            }
          />
        ) : null}

        {/* Publish button */}
        <View style={[a.gap_sm]}>
          <Button
            label={l`Publish FlashBook`}
            size="large"
            variant="solid"
            color="primary"
            disabled={isPending || isSuccess}
            onPress={onPublish}
            testID="publishFlashBookBtn">
            <ButtonText>
              {forClassroom
                ? l`Publish into the classroom`
                : l`Publish FlashBook`}
            </ButtonText>
            <ButtonIcon
              icon={isSuccess ? CheckThin : isPending ? Loader : PaperPlane}
            />
          </Button>
        </View>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}

function OptionRow({label, onSelect}: {label: string; onSelect?: () => void}) {
  const t = useTheme()
  return (
    <Button label={label} onPress={onSelect} disabled={!onSelect}>
      {({hovered, pressed}) => (
        <View
          style={[
            a.w_full,
            a.px_md,
            a.py_sm,
            a.rounded_sm,
            a.border,
            t.atoms.bg_contrast_25,
            hovered || pressed
              ? [t.atoms.border_contrast_high]
              : [t.atoms.border_contrast_low],
          ]}>
          <Text style={[a.text_md, a.font_bold, a.leading_snug]}>{label}</Text>
        </View>
      )}
    </Button>
  )
}
